<template>
  <div class="printer-view">
    <header class="printer-header">
      <div>
        <p class="overline">Centre d'impression POS</p>
        <h1>Prévisualisation du ticket</h1>
        <p class="subtitle">
          Vérifiez les informations avant d'envoyer le ticket vers votre
          imprimante POS.
        </p>
        <p v-if="reference" class="current-ref">Référence : {{ reference }}</p>
      </div>
      <div class="header-actions">
        <button
          class="btn btn-outline"
          @click="refreshPreview"
          :disabled="loading"
        >
          <i class="bi bi-arrow-clockwise"></i>
          <span>Actualiser</span>
        </button>
        <button
          class="btn btn-primary"
          @click="printTicket"
          :disabled="!hasSale || loading"
        >
          <i class="bi bi-printer"></i>
          <span>Imprimer</span>
        </button>
      </div>
    </header>

    <main class="printer-body">
      <section class="preview-section">
        <div v-if="loading" class="preview-state">
          <div class="spinner"></div>
          <p>Chargement du ticket…</p>
        </div>
        <div v-else-if="!hasSale" class="preview-state">
          <i class="bi bi-receipt-cutoff"></i>
          <h3>Aucun ticket à afficher</h3>
          <p>Choisissez une vente et relancez la prévisualisation.</p>
        </div>
        <div v-else class="ticket-preview" ref="ticket">
          <div class="ticket-header">
            <h2>{{ header.title }}</h2>
            <p>{{ header.address }}</p>
            <p>{{ header.phone }}</p>
          </div>

          <div class="ticket-meta">
            <div>
              <span class="label">Ticket</span>
              <strong>{{ sale.reference }}</strong>
            </div>
            <div>
              <span class="label">Date</span>
              <strong>{{ formattedDate }}</strong>
            </div>
            <div>
              <span class="label">Client</span>
              <strong>{{ sale.customer || "Client comptant" }}</strong>
            </div>
          </div>

          <div class="ticket-divider"></div>

          <div class="items-section">
            <div class="items-header">
              <span>Article</span>
              <span>Qté</span>
              <span>Prix</span>
              <span>Total</span>
            </div>
            <ul class="ticket-items" v-if="previewItems.length">
              <li v-for="item in previewItems" :key="item.key" class="item-row">
                <div class="item-name">
                  <strong>{{ item.name }}</strong>
                </div>
                <div class="item-qty">{{ item.quantity }}</div>
                <div class="item-price">{{ formatCurrency(item.price) }}</div>
                <div class="item-total">{{ formatCurrency(item.total) }}</div>
              </li>
            </ul>
            <div v-else class="empty-items">
              <i class="bi bi-list-ul"></i>
              <span>Aucun article renseigné pour ce ticket.</span>
            </div>
          </div>

          <div class="ticket-divider dotted"></div>

          <div class="ticket-totals">
            <div class="row">
              <span>Sous-total</span>
              <strong>{{ formatCurrency(subtotal) }}</strong>
            </div>
            <div class="row">
              <span>TVA</span>
              <strong>{{ formatCurrency(taxAmount) }}</strong>
            </div>
            <div class="row total">
              <span>Total TTC</span>
              <strong>{{ formatCurrency(total) }}</strong>
            </div>
            <div class="row paid">
              <span>Payé</span>
              <strong>{{ formatCurrency(paid) }}</strong>
            </div>
            <div class="row unpaid" v-if="unpaid > 0">
              <span>Restant</span>
              <strong>{{ formatCurrency(unpaid) }}</strong>
            </div>
          </div>

          <div class="ticket-divider"></div>

          <div class="ticket-footer">
            <p>Merci pour votre confiance</p>
            <p>{{ footer.website }}</p>
          </div>
        </div>
      </section>

      <section class="pos-section">
        <div class="pos-card">
          <h3>Impression sur imprimante POS</h3>
          <p>
            Lorsque vous êtes satisfait de la prévisualisation, utilisez votre
            imprimante POS pour générer le ticket physique.
          </p>
          <POSPrinter />
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import POSPrinter from "../../components/cards/POSPrinter.vue";

const pickFirst = (...values) => {
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (value !== null && value !== undefined) {
      return value;
    }
  }
  return null;
};

export default {
  name: "PrinterView",
  components: {
    POSPrinter,
  },
  data() {
    return {
      loading: false,
      reference: this.$route.query.reference || null,
      header: {
        title: "Peascom Caisse Manager",
        address: "Zone Industrielle, Casablanca",
        phone: "+212 600-302731",
      },
      footer: {
        website: "www.ikhwanfood.ma",
      },
    };
  },
  computed: {
    ...mapGetters("sellerSale", {
      currentSale: "getSellerSale",
    }),
    hasSale() {
      if (!this.sale.reference || this.sale.reference === "N/A") {
        return false;
      }
      if (this.previewItems.length > 0) {
        return true;
      }
      const hasTotals =
        Number(this.sale.totalTTC || 0) > 0 ||
        Number(this.sale.totalHT || 0) > 0 ||
        Number(this.sale.paid || 0) > 0;
      return hasTotals;
    },
    sale() {
      const raw = this.currentSale || {};
      const customerObject = raw.customer && raw.customer.fullName;
      const reference =
        pickFirst(raw.reference, raw.sale_reference, this.reference) || "N/A";
      const totalPriceHT = pickFirst(
        raw.totalPriceHT,
        raw.total_price_ht,
        raw.total_ht
      );
      const totalPriceTTC = pickFirst(
        raw.totalPriceTTC,
        raw.total_price_ttc,
        raw.total_amount,
        raw.total,
        0
      );
      const totalTVA = pickFirst(
        raw.totalTVA,
        raw.total_vat,
        raw.total_tva,
        totalPriceHT !== null && totalPriceHT !== undefined
          ? totalPriceTTC - totalPriceHT
          : 0
      );
      const paidAmount = pickFirst(
        raw.amountPaid,
        raw.amount_paid,
        raw.paid_amount,
        0
      );
      const unpaidAmount = pickFirst(
        raw.amountUnpaid,
        raw.amount_unpaid,
        raw.remaining_amount,
        0
      );
      return {
        reference,
        date: pickFirst(raw.date, raw.sale_date, raw.created_at, null),
        customer: pickFirst(
          raw.customerFullName,
          raw.customer_full_name,
          customerObject,
          raw.customer_name,
          raw.seller_customer_reference,
          null
        ),
        totalTTC: totalPriceTTC || 0,
        totalHT: totalPriceHT,
        tax: totalTVA || 0,
        paid: paidAmount || 0,
        unpaid: unpaidAmount || 0,
        items: raw.saleItems || raw.items || [],
      };
    },
    previewItems() {
      if (!Array.isArray(this.sale.items)) return [];
      return this.sale.items.map((item, index) => {
        const qtyValue = pickFirst(
          item.quantity,
          item.qty,
          item.item_quantity,
          0
        );
        const priceValue = pickFirst(
          item.priceTTC,
          item.price_ttc,
          item.price,
          item.unit_price,
          0
        );
        const qty = Number(qtyValue) || 0;
        const price = Number(priceValue) || 0;
        const totalValue = pickFirst(
          item.totalPriceTTC,
          item.total_price_ttc,
          qty * price,
          0
        );
        return {
          key: item.item_reference || item.reference || index,
          name:
            item.itemName ||
            item.item_name ||
            item.itemFullName ||
            item.reference ||
            `Article ${index + 1}`,
          quantity: qty,
          price,
          total: Number(totalValue) || 0,
        };
      });
    },
    subtotal() {
      if (this.sale.totalHT != null) return Number(this.sale.totalHT);
      const totalItem = this.previewItems.reduce(
        (sum, item) => sum + item.total,
        0
      );
      return totalItem;
    },
    taxAmount() {
      return Number(pickFirst(this.sale.tax, 0));
    },
    total() {
      const totalValue = pickFirst(
        this.sale.totalTTC,
        this.subtotal + this.taxAmount
      );
      return Number(totalValue) || 0;
    },
    paid() {
      return Number(pickFirst(this.sale.paid, 0));
    },
    unpaid() {
      const unpaidValue = pickFirst(
        this.sale.unpaid,
        Math.max(this.total - this.paid, 0)
      );
      return Number(unpaidValue) || 0;
    },
    formattedDate() {
      if (!this.sale.date) return "—";
      try {
        return new Date(this.sale.date).toLocaleString("fr-FR");
      } catch (error) {
        return this.sale.date;
      }
    },
  },
  watch: {
    "$route.query.reference": {
      immediate: true,
      handler(value) {
        this.reference = value || null;
        this.loadSale();
      },
    },
  },
  methods: {
    formatCurrency(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "0";
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
      });
    },
    async loadSale() {
      if (!this.reference) {
        this.$store.commit("sellerSale/resetSellerSale");
        return;
      }
      this.loading = true;
      try {
        await this.$store.dispatch("sellerSale/show", this.reference);
      } catch (error) {
        console.error("Erreur de chargement du ticket", error);
        if (this.$toast && typeof this.$toast.error === "function") {
          this.$toast.error(
            "Impossible de récupérer les informations de la vente."
          );
        }
      } finally {
        this.loading = false;
      }
    },
    refreshPreview() {
      this.loadSale();
    },
    printTicket() {
      if (!this.$refs.ticket) return;
      const printWindow = window.open("", "_blank", "width=420,height=640");
      if (!printWindow) {
        if (this.$toast && typeof this.$toast.error === "function") {
          this.$toast.error("Impossible d'ouvrir la fenêtre d'impression.");
        }
        return;
      }
      const styles = `
        <style>
          body { font-family: 'Courier New', monospace; padding: 16px; }
          .ticket-preview { width: 320px; margin: 0 auto; }
          .ticket-preview * { font-family: inherit; }
          .ticket-divider { border-top: 1px dashed #555; margin: 12px 0; }
          .ticket-divider.dotted { border-top-style: dotted; }
        </style>
      `;
      printWindow.document.write(`
        <html>
          <head>
            <title>Ticket ${this.sale.reference}</title>
            ${styles}
          </head>
          <body>
            ${this.$refs.ticket.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    },
  },
};
</script>

<style scoped>
.printer-view {
  min-height: 100vh;
  background: #f3f4f8;
  padding: 2rem clamp(1rem, 3vw, 3rem);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.printer-header {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.overline {
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.75rem;
  color: #6366f1;
  margin-bottom: 0.5rem;
}

.printer-header h1 {
  margin: 0;
  font-size: 2rem;
  color: #0f172a;
}

.subtitle {
  margin: 0.4rem 0;
  color: #475569;
  max-width: 520px;
}

.current-ref {
  margin: 0.2rem 0 0;
  font-weight: 600;
  color: #2563eb;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.printer-body {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 1.5rem;
  align-items: start;
}

.preview-section {
  background: #fff;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
  min-height: 520px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-state {
  text-align: center;
  color: #64748b;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
}

.preview-state i {
  font-size: 2.5rem;
  color: #a5b4fc;
}

.spinner {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 4px solid rgba(99, 102, 241, 0.2);
  border-top-color: #6366f1;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.ticket-preview {
  width: 320px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  font-family: "Courier New", monospace;
  color: #111827;
}

.ticket-header {
  text-align: center;
  margin-bottom: 1rem;
}

.ticket-header h2 {
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.ticket-header p {
  margin: 0.1rem 0;
  font-size: 0.8rem;
}

.ticket-meta {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.35rem;
  font-size: 0.85rem;
}

.ticket-meta .label {
  text-transform: uppercase;
  font-size: 0.65rem;
  letter-spacing: 0.05em;
  color: #94a3b8;
}

.ticket-divider {
  border-top: 1px dashed #cbd5f5;
  margin: 1rem 0;
}

.ticket-divider.dotted {
  border-top-style: dotted;
}

.items-section {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.25rem 0.5rem;
  background: #fafafa;
}

.items-header {
  display: grid;
  grid-template-columns: 2fr 0.6fr 0.9fr 0.9fr;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  padding: 0.4rem 0.2rem;
  border-bottom: 1px dashed #d6d6d6;
}

.ticket-items {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
}

.item-row {
  display: grid;
  grid-template-columns: 2fr 0.6fr 0.9fr 0.9fr;
  align-items: center;
  font-size: 0.82rem;
  padding: 0.35rem 0.2rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
}

.item-row:last-child {
  border-bottom: none;
}

.item-name strong {
  display: block;
  font-size: 0.85rem;
}

.item-qty,
.item-price,
.item-total {
  text-align: right;
  font-family: "Courier New", monospace;
}

.item-total {
  font-weight: 700;
}

.empty-items {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  color: #9ca3af;
  font-size: 0.8rem;
}

.empty-items i {
  font-size: 1rem;
}

.ticket-totals {
  font-size: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.ticket-totals .row {
  display: flex;
  justify-content: space-between;
}

.ticket-totals .total {
  font-size: 1rem;
  font-weight: 700;
}

.ticket-totals .paid {
  color: #16a34a;
}

.ticket-totals .unpaid {
  color: #dc2626;
}

.ticket-footer {
  text-align: center;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
}

.pos-section {
  width: 100%;
}

.pos-card {
  background: #0f172a;
  color: #f8fafc;
  border-radius: 20px;
  padding: 1.75rem;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.pos-card h3 {
  margin: 0;
}

.pos-card p {
  margin: 0;
  color: #cbd5f5;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.2rem;
  border-radius: 12px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: #fff;
}

.btn-outline {
  border: 1px solid rgba(99, 102, 241, 0.4);
  background: transparent;
  color: #4f46e5;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

@media (max-width: 900px) {
  .printer-body {
    grid-template-columns: 1fr;
  }
}
</style>
